import { supabase } from '@/lib/supabase';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  AlertTriangle,
  Camera,
  Check,
  ChevronDown,
  EyeOff,
  Image,
  Maximize,
  Redo2,
  RefreshCw,
  Save,
  Settings,
  Trash2,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { selectIsDirty, selectTool, selectZoom } from '../mapEditorSelectors';
import { redo, setBackgroundImage, setTool, setZoom, undo } from '../mapEditorSlice';

// ============================================
// ICONOS SVG PERSONALIZADOS
// ============================================

type IconProps = {
  className?: string;
};

async function downscaleImageToJpeg(
  file: File,
  maxDimension: number,
  quality: number
): Promise<File> {
  try {
    if (!file.type.startsWith('image/')) return file;

    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close();

    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (!b) reject(new Error('No se pudo generar la imagen optimizada'));
          else resolve(b);
        },
        'image/jpeg',
        quality
      );
    });

    const optimizedName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
    return new File([blob], optimizedName, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

function PointerIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4 3.5 17 9.2 12.8 11l2.4 7.8-2.9.9-2.1-6.8-3.9 3.4z" />
    </svg>
  );
}

function CircleIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="7" />
    </svg>
  );
}

function RectangleIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="5" y="7" width="14" height="10" rx="2" ry="2" />
    </svg>
  );
}

function PolygonIcon({ className = 'w-4 h-4' }: IconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon points="12 5 20 19 4 19" />
    </svg>
  );
}

// ============================================
// TOOLTIP COMPONENT
// ============================================

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  shortcut?: string;
}

function Tooltip({ children, content, shortcut }: TooltipProps) {
  return (
    <div className="relative group">
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 pointer-events-none">
        {content}
        {shortcut && <span className="ml-1.5 text-slate-400">({shortcut})</span>}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900" />
      </div>
    </div>
  );
}

// ============================================
// CONFIGURACIÓN DE HERRAMIENTAS
// ============================================

const drawTools = [
  {
    id: 'select' as const,
    label: 'Seleccionar',
    shortcut: 'V',
    icon: <PointerIcon className="w-4 h-4" />,
  },
  {
    id: 'rect' as const,
    label: 'Mesa Rectangular',
    shortcut: 'R',
    icon: <RectangleIcon className="w-4 h-4" />,
  },
  {
    id: 'circle' as const,
    label: 'Mesa Circular',
    shortcut: 'C',
    icon: <CircleIcon className="w-4 h-4" />,
  },
  {
    id: 'polygon' as const,
    label: 'Mesa Poligonal',
    shortcut: 'P',
    icon: <PolygonIcon className="w-4 h-4" />,
  },
];

// Herramientas de overlay (áreas de ocultación)
const overlayTools = [
  {
    id: 'overlay-rect' as const,
    label: 'Área Ocultación Rect.',
    shortcut: 'O',
    icon: <EyeOff className="w-4 h-4" />,
  },
  {
    id: 'overlay-circle' as const,
    label: 'Área Ocultación Círc.',
    shortcut: 'Shift+O',
    icon: <EyeOff className="w-4 h-4" />,
  },
];

type ToolId = (typeof drawTools)[number]['id'] | (typeof overlayTools)[number]['id'];

// ============================================
// PROPS INTERFACE
// ============================================

interface ToolbarProps {
  onSave: () => void;
  onGenerateSeats: () => void;
  onCleanupSeats: () => void;
  onRegenerateAll?: () => void;
  isSaving?: boolean;
  eventId?: string;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onZoomReset?: () => void;
}

export default function Toolbar({
  onSave,
  onGenerateSeats,
  onCleanupSeats,
  onRegenerateAll,
  isSaving,
  eventId,
  onZoomIn,
  onZoomOut,
  onZoomReset,
}: ToolbarProps) {
  const dispatch = useAppDispatch();
  const activeTool = useAppSelector(selectTool);
  const isDirty = useAppSelector(selectIsDirty);
  const zoom = useAppSelector(selectZoom);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados para dropdowns
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement | null>(null);
  const optionsMenuRef = useRef<HTMLDivElement | null>(null);

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (showExportMenu && exportMenuRef.current && !exportMenuRef.current.contains(target)) {
        setShowExportMenu(false);
      }
      if (showOptionsMenu && optionsMenuRef.current && !optionsMenuRef.current.contains(target)) {
        setShowOptionsMenu(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showExportMenu, showOptionsMenu]);

  // Cerrar dropdowns con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowExportMenu(false);
        setShowOptionsMenu(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleToolClick = (toolId: ToolId) => {
    dispatch(setTool(toolId));
  };

  const handleUndo = () => {
    dispatch(undo());
  };

  const handleRedo = () => {
    dispatch(redo());
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // === ACCIONES AVANZADAS CON CONFIRMACIÓN ===

  const handleRecalculateSeats = () => {
    setShowOptionsMenu(false);
    // Doble confirmación para acción peligrosa
    const confirmed = window.confirm(
      'RECALCULAR ASIENTOS\n\n' +
        'Esta acción regenerará todos los asientos basándose en el mapa actual.\n\n' +
        '• Los asientos vendidos NO serán afectados\n' +
        '• Los asientos temporalmente bloqueados se conservarán\n' +
        '• Se crearán nuevos asientos para mesas nuevas\n\n' +
        '¿Deseas continuar?'
    );
    if (confirmed && onGenerateSeats) {
      onGenerateSeats();
    }
  };

  const handleDeleteDistribution = () => {
    setShowOptionsMenu(false);
    // Doble confirmación para acción destructiva
    const firstConfirm = window.confirm(
      'ELIMINAR DISTRIBUCIÓN\n\n' +
        'ADVERTENCIA: Esta acción es PELIGROSA.\n\n' +
        'Eliminará todos los asientos que NO estén vendidos.\n' +
        'Los asientos vendidos se conservarán por seguridad.\n\n' +
        '¿Estás seguro de continuar?'
    );

    if (firstConfirm) {
      const secondConfirm = window.confirm(
        'SEGUNDA CONFIRMACIÓN REQUERIDA\n\n' +
          'Estás a punto de eliminar la distribución de asientos.\n' +
          'Esta acción NO se puede deshacer fácilmente.\n\n' +
          '¿Confirmas que deseas continuar?'
      );

      if (secondConfirm && onCleanupSeats) {
        onCleanupSeats();
      }
    }
  };

  const handleRegenerateAll = () => {
    setShowOptionsMenu(false);
    // Triple confirmación para la acción más peligrosa
    if (onRegenerateAll) {
      onRegenerateAll();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      alert('La imagen debe ser menor a 15MB');
      return;
    }

    try {
      const optimizedFile = await downscaleImageToJpeg(file, 1920, 0.8);
      if (optimizedFile.size > 5 * 1024 * 1024) {
        alert('La imagen optimizada debe ser menor a 5MB');
        return;
      }

      const fileExt = optimizedFile.name.split('.').pop();
      const fileName = `${eventId || 'temp'}-${Date.now()}.${fileExt}`;
      const filePath = `venue-maps/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('event-assets')
        .upload(filePath, optimizedFile, { cacheControl: '31536000', upsert: false });

      if (uploadError) {
        alert('Error al subir la imagen: ' + uploadError.message);
        return;
      }

      const { data: publicUrlData } = supabase.storage.from('event-assets').getPublicUrl(filePath);

      dispatch(setBackgroundImage(publicUrlData.publicUrl));
      alert('Imagen de fondo cargada exitosamente');
    } catch (err) {
      console.error('Error uploading background image:', err);
      alert('Error al cargar la imagen');
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handlers de zoom internos si no se pasan desde el padre
  const handleZoomIn = () => {
    if (onZoomIn) {
      onZoomIn();
    } else {
      const newZoom = Math.min(3, zoom + 0.1);
      dispatch(setZoom(newZoom));
    }
  };

  const handleZoomOut = () => {
    if (onZoomOut) {
      onZoomOut();
    } else {
      const newZoom = Math.max(0.1, zoom - 0.1);
      dispatch(setZoom(newZoom));
    }
  };

  const handleZoomReset = () => {
    if (onZoomReset) {
      onZoomReset();
    } else {
      dispatch(setZoom(1));
    }
  };

  // ============================================
  // RENDER: LAYOUT DE 3 ZONAS
  // ============================================

  return (
    <div className="relative z-30 mx-2 md:mx-4 my-2">
      {/* Toolbar flotante con glassmorphism */}
      <div className="bg-white/90 backdrop-blur-md shadow-lg border border-slate-200 rounded-xl px-2 md:px-4 py-2">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          {/* ========== ZONA IZQUIERDA: HERRAMIENTAS ========== */}
          <div className="flex items-center gap-1 md:gap-2">
            {/* Herramientas de dibujo */}
            <div className="flex items-center gap-0.5 md:gap-1 bg-slate-100 rounded-lg p-1">
              {drawTools.map((tool) => (
                <Tooltip key={tool.id} content={tool.label} shortcut={tool.shortcut}>
                  <button
                    onClick={() => handleToolClick(tool.id)}
                    className={`
                      flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md transition-all duration-150
                      ${
                        activeTool === tool.id
                          ? 'bg-indigo-600 text-white shadow-md'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                      }
                    `}
                  >
                    {tool.icon}
                  </button>
                </Tooltip>
              ))}
            </div>

            {/* Separador visual */}
            <div className="w-px h-6 bg-slate-200 hidden md:block" />

            {/* Herramientas de overlay (áreas de ocultación) */}
            <div className="flex items-center gap-0.5 md:gap-1 bg-slate-100 rounded-lg p-1">
              {overlayTools.map((tool) => (
                <Tooltip key={tool.id} content={tool.label} shortcut={tool.shortcut}>
                  <button
                    onClick={() => handleToolClick(tool.id)}
                    className={`
                      flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md transition-all duration-150
                      ${
                        activeTool === tool.id
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                      }
                    `}
                  >
                    {tool.icon}
                  </button>
                </Tooltip>
              ))}
            </div>

            {/* Separador visual */}
            <div className="w-px h-6 bg-slate-200 hidden md:block" />

            {/* Subir imagen de fondo */}
            <Tooltip content="Subir imagen de fondo">
              <button
                onClick={handleUploadClick}
                className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all"
              >
                <Image className="w-4 h-4" />
              </button>
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* ========== ZONA CENTRO: NAVEGACIÓN ========== */}
          <div className="flex items-center gap-1 md:gap-2">
            {/* Undo/Redo */}
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1">
              <Tooltip content="Deshacer" shortcut="Ctrl+Z">
                <button
                  onClick={handleUndo}
                  className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all"
                >
                  <Undo2 className="w-4 h-4" />
                </button>
              </Tooltip>
              <Tooltip content="Rehacer" shortcut="Ctrl+Y">
                <button
                  onClick={handleRedo}
                  className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all"
                >
                  <Redo2 className="w-4 h-4" />
                </button>
              </Tooltip>
            </div>

            {/* Separador visual */}
            <div className="w-px h-6 bg-slate-200 hidden md:block" />

            {/* Zoom Controls */}
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1">
              <Tooltip content="Alejar" shortcut="-">
                <button
                  onClick={handleZoomOut}
                  className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
              </Tooltip>
              <Tooltip content="Restablecer zoom">
                <button
                  onClick={handleZoomReset}
                  className="flex items-center justify-center min-w-[48px] h-8 md:h-9 px-2 rounded-md text-xs font-medium text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all"
                >
                  {Math.round(zoom * 100)}%
                </button>
              </Tooltip>
              <Tooltip content="Acercar" shortcut="+">
                <button
                  onClick={handleZoomIn}
                  className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm transition-all"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </Tooltip>
            </div>

            {/* Fit to screen (ajustar contenido al viewport) */}
            <Tooltip content="Ajustar a pantalla" shortcut="0">
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('mapEditor:fitToScreen'))}
                className="hidden md:flex items-center justify-center w-9 h-9 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all"
              >
                <Maximize className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>

          {/* ========== ZONA DERECHA: ACCIONES FINALES ========== */}
          <div className="flex items-center gap-1 md:gap-2">
            {/* Botón de exportación de imagen */}
            <div className="relative" ref={exportMenuRef}>
              <Tooltip content="Exportar imagen">
                <button
                  onClick={() => setShowExportMenu((v) => !v)}
                  className="flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-all"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </Tooltip>

              {showExportMenu && (
                <div className="absolute z-[100] mt-2 right-0 bg-white border border-slate-200 rounded-lg shadow-xl w-56 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Exportar Imagen
                    </span>
                  </div>
                  <div className="py-1">
                    <button
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent('mapEditor:exportImage', { detail: { scale: 1 } })
                        );
                        setShowExportMenu(false);
                      }}
                    >
                      Guardar imagen
                    </button>
                    <button
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent('mapEditor:copyImage', { detail: { scale: 1 } })
                        );
                        setShowExportMenu(false);
                      }}
                    >
                      Copiar al portapapeles
                    </button>
                    <button
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent('mapEditor:exportStatsImage', { detail: { scale: 1 } })
                        );
                        setShowExportMenu(false);
                      }}
                    >
                      Guardar con estadísticas
                    </button>
                  </div>
                  <div className="border-t border-slate-200 py-1">
                    <button
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent('mapEditor:exportImage', { detail: { scale: 2 } })
                        );
                        setShowExportMenu(false);
                      }}
                    >
                      Alta calidad (2x)
                    </button>
                    <button
                      className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent('mapEditor:exportImage', { detail: { scale: 3 } })
                        );
                        setShowExportMenu(false);
                      }}
                    >
                      Máxima calidad (3x)
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Menú de Opciones Avanzadas (acciones peligrosas) */}
            <div className="relative" ref={optionsMenuRef}>
              <Tooltip content="Opciones avanzadas">
                <button
                  onClick={() => setShowOptionsMenu((v) => !v)}
                  className={`
                    flex items-center justify-center gap-1 px-2 md:px-3 h-8 md:h-9 rounded-md transition-all
                    ${
                      showOptionsMenu
                        ? 'bg-slate-200 text-slate-900'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }
                  `}
                >
                  <Settings className="w-4 h-4" />
                  <span className="hidden md:inline text-sm font-medium">Opciones</span>
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${showOptionsMenu ? 'rotate-180' : ''}`}
                  />
                </button>
              </Tooltip>

              {showOptionsMenu && (
                <div className="absolute z-[100] mt-2 right-0 bg-white border border-slate-200 rounded-lg shadow-xl w-64 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Acciones Avanzadas
                    </span>
                  </div>

                  {/* Recalcular Asientos */}
                  <div className="py-1">
                    <button
                      className="flex items-center gap-3 w-full text-left px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                      onClick={handleRecalculateSeats}
                    >
                      <RefreshCw className="w-4 h-4 text-indigo-600" />
                      <div>
                        <div className="font-medium">Recalcular Asientos</div>
                        <div className="text-xs text-slate-500">Sincroniza mapa con BD</div>
                      </div>
                    </button>
                  </div>

                  {/* Sección de peligro */}
                  <div className="border-t border-slate-200">
                    <div className="px-3 py-1.5 bg-red-50 border-b border-red-100">
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                        <span className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">
                          Zona de Peligro
                        </span>
                      </div>
                    </div>

                    {onRegenerateAll && (
                      <button
                        className="flex items-center gap-3 w-full text-left px-3 py-2.5 text-sm text-amber-700 hover:bg-amber-50 transition-colors"
                        onClick={handleRegenerateAll}
                      >
                        <RefreshCw className="w-4 h-4" />
                        <div>
                          <div className="font-medium">Regenerar Todo</div>
                          <div className="text-xs text-amber-600">Para eventos sin ventas</div>
                        </div>
                      </button>
                    )}

                    <button
                      className="flex items-center gap-3 w-full text-left px-3 py-2.5 text-sm text-red-700 hover:bg-red-50 transition-colors"
                      onClick={handleDeleteDistribution}
                    >
                      <Trash2 className="w-4 h-4" />
                      <div>
                        <div className="font-medium">Eliminar Distribución</div>
                        <div className="text-xs text-red-500">Borra asientos no vendidos</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Separador visual */}
            <div className="w-px h-6 bg-slate-200" />

            {/* Botón GUARDAR (principal) */}
            <Tooltip content={isDirty ? 'Guardar cambios' : 'Sin cambios pendientes'}>
              <button
                onClick={onSave}
                disabled={!isDirty || isSaving}
                className={`
                  flex items-center justify-center gap-1.5 px-3 md:px-4 h-8 md:h-9 rounded-lg font-medium text-sm transition-all duration-150
                  ${
                    isDirty && !isSaving
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }
                `}
              >
                {isSaving ? (
                  <>
                    <Save className="w-4 h-4 animate-pulse" />
                    <span className="hidden md:inline">Guardando...</span>
                  </>
                ) : isDirty ? (
                  <>
                    <Save className="w-4 h-4" />
                    <span className="hidden md:inline">Guardar Diseño</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span className="hidden md:inline">Guardado</span>
                  </>
                )}
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}
